// src/components/Search/SearchBar.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaCompass, FaPlay, FaDownload, FaImdb, FaTv, FaServer } from 'react-icons/fa';

// List of engaging placeholder texts
const placeholderOptions = [
  'What do you want to Watch?',
  'Search for Movies or TV Shows...',
  'Looking for something to Watch?',
  'Find your next favorite Show or Movie!',
  'Enter a Movie or TV show title...',
];

// Define filters for servers outside the component to ensure stability
const serverFiltersConfig = {
  Imdb: [
    { name: 'limit', label: 'Limit', type: 'number', required: false },
    { name: 'yearfrom', label: 'Year From', type: 'number', required: false },
    { name: 'yearto', label: 'Year To', type: 'number', required: false },
    { name: 'type', label: 'Type', type: 'select', options: ['movie', 'tv'], required: false },
  ],
  Tmdb: [
    { name: 'limit', label: 'Limit', type: 'number', required: false },
    { name: 'yearfrom', label: 'Year From', type: 'number', required: false },
    { name: 'yearto', label: 'Year To', type: 'number', required: false },
    { name: 'type', label: 'Type', type: 'select', options: ['movie', 'tv'], required: false },
  ],
  S1: [
    { name: 'year', label: 'Year', type: 'number', required: false },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: ['movie', 'tv'],
      required: true,
    },
  ]
};

export default function SearchBar({
  type,
  server,
  onSearch,
  onFocus,
  setType,
  setServer,
  activeServers,
  onModeOrServerChange,
}) {
  const [formValues, setFormValues] = useState({ query: '' }); // Initialize with query
  const [placeholder, setPlaceholder] = useState('');
  const [showSeason, setShowSeason] = useState(false); // For S1 "tv" => show season
  const [error, setError] = useState(''); // For empty query

  // Reference to the main input
  const inputRef = useRef(null);

  // Memoize server filters
  const serverFilters = useMemo(() => serverFiltersConfig, []);

  // The main search field definition
  const mainQueryField = { name: 'query', label: 'Search', type: 'text', required: true };

  // Initialize filters based on current server
  const initialFilters = useMemo(
    () => serverFilters[server] || [],
    [server, serverFilters]
  );

  // Pick a random placeholder on mount
  useEffect(() => {
    setPlaceholder(
      placeholderOptions[Math.floor(Math.random() * placeholderOptions.length)]
    );
  }, []);

  // Decide whether to show "Season" input for S1 when type=Download & user selected "tv"
  useEffect(() => {
    const shouldShowSeason =
      server === 'S1' && type === 'Download' && formValues.type === 'tv';
    setShowSeason(shouldShowSeason);
  }, [server, type, formValues.type]);

  // Combine standard filters + optional "Season"
  const combinedFilters = useMemo(() => {
    let filters = [...initialFilters];
    if (showSeason) {
      const typeIndex = filters.findIndex((f) => f.name === 'type');
      if (typeIndex !== -1) {
        filters.splice(typeIndex + 1, 0, {
          name: 'season',
          label: 'Season',
          type: 'number',
          required: false,
        });
      } else {
        filters.push({
          name: 'season',
          label: 'Season',
          type: 'number',
          required: false,
        });
      }
    }
    return filters;
  }, [initialFilters, showSeason]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Handle clearing the query
  const handleClear = () => {
    setFormValues((prev) => ({ ...prev, query: '' }));
    if (inputRef.current) {
      inputRef.current.focus();
    }
    // Also trigger search with empty query to reset results
    onSearch({ ...formValues, query: '' }, server);
  };

  // Handle form submit => main search
  const handleSubmit = (e) => {
    e.preventDefault();
    if (type !== 'Discover' && !formValues.query.trim()) {
      setError('Please fill out the Search Query');
      return;
    }
    setError('');
    onSearch(formValues, server);
  };

  // --------------------------------------------------
  // SWITCH MODES (Discover/Stream/Download)
  // --------------------------------------------------
  const pickRandomServer = (mode) => {
    if (mode === 'Stream') {
      const streamServers = ['Imdb', 'Tmdb'].filter((s) => activeServers[s]);
      if (streamServers.length > 0) {
        return streamServers[Math.floor(Math.random() * streamServers.length)];
      }
      return '';
    } else if (mode === 'Download') {
      const downloadServers = ['S1', 'S2', 'S3'].filter((s) => activeServers[s]);
      if (downloadServers.length > 0) {
        return downloadServers[Math.floor(Math.random() * downloadServers.length)];
      }
      return '';
    }
    return '';
  };

  const handleModeChange = (selectedType) => {
    if (selectedType === type) return; // No change if already that mode

    let newServer = server;

    if (selectedType === 'Discover') {
      // When going to Discover, clear query & server
      setFormValues({ query: '' }); // Ensure query is defined
      newServer = '';
      setServer('');
    } else {
      // If switching to Stream/Download, ensure server is valid
      const validServers =
        selectedType === 'Stream' ? ['Imdb', 'Tmdb'] : ['S1', 'S2', 'S3'];
      if (!validServers.includes(server)) {
        newServer = pickRandomServer(selectedType);
        setServer(newServer);
      }
    }

    setType(selectedType);
    onModeOrServerChange(selectedType, newServer);

    // Trigger an immediate search in the new mode
    if (selectedType === 'Discover') {
      // Let the main content do discover fetching
      onSearch({ query: '' }, '');
    } else {
      // Use the existing form values or empty if none
      onSearch(formValues, newServer);
    }
  };

  // --------------------------------------------------
  // SWITCH SERVER
  // --------------------------------------------------
  const handleServerChange = (selectedServer) => {
    if (selectedServer === server) return;
    setServer(selectedServer);
    onModeOrServerChange(type, selectedServer);

    // Trigger search again if not discover
    if (type !== 'Discover') {
      onSearch(formValues, selectedServer);
    }
  };

  // Servers available for current type
  const availableServers = useMemo(() => {
    if (type === 'Stream') {
      return ['Imdb', 'Tmdb'].filter((s) => activeServers[s]);
    } else if (type === 'Download') {
      return ['S1', 'S2', 'S3'].filter((s) => activeServers[s]);
    }
    return [];
  }, [type, activeServers]);

  // Icons for modes
  const modeIcons = {
    Discover: <FaCompass size={20} />,
    Stream: <FaPlay size={20} />,
    Download: <FaDownload size={20} />,
  };

  // Icons for servers
  const serverIcons = {
    Imdb: <FaImdb size={20} />,
    Tmdb: <FaTv size={20} />,
    S1: <FaServer size={20} />,
    S2: <FaServer size={20} />,
    S3: <FaServer size={20} />,
  };

  // Utility to check if a server is a "stream" server
  const isStreamServer = (serverName) => ['Imdb', 'Tmdb'].includes(serverName);

  // Utility to pick a hover color
  const getHoverColor = (serverName) => {
    return isStreamServer(serverName)
      ? 'hover:bg-purple-400'
      : 'hover:bg-blue-400';
  };

  // --------------------------------------------------
  // RESET FORM WHEN TYPE OR SERVER CHANGES EXTERNALLY
  // --------------------------------------------------
  useEffect(() => {
    setFormValues({ query: '' }); // Ensure query is defined
    setError('');
  }, [type, server]);

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit}
        onFocus={onFocus}
        className="p-6 rounded-lg animate-fadeInSlow w-full bg-gray-900 shadow-lg relative"
      >
        {/* MAIN SEARCH INPUT & BUTTON */}
        <div className="flex flex-col lg:flex-row lg:items-center mb-6 space-y-4 lg:space-y-0 lg:space-x-4">
          {/* The search box */}
          <div className="flex-1 relative">
            <label className="block text-sm text-gray-300 mb-1" htmlFor="query">
              {mainQueryField.label}
            </label>
            <div className="relative">
              {/* Mode Icon */}
              {modeIcons[type] && (
                <span className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400">
                  {modeIcons[type]}
                </span>
              )}
              <input
                ref={inputRef}
                type={mainQueryField.type}
                name={mainQueryField.name}
                id="query"
                placeholder={placeholder}
                value={
                  type === 'Discover'
                    ? '' // read-only & empty in discover
                    : formValues[mainQueryField.name] || ''
                }
                onChange={handleChange}
                className="w-full pl-10 pr-10 py-3 bg-gray-800 text-textPrimary rounded-md focus:outline-none focus:ring-2 focus:ring-accentBlue transition duration-300"
                required={type !== 'Discover' ? mainQueryField.required : false}
                readOnly={type === 'Discover'} // no manual input in discover
              />
              {formValues.query && type !== 'Discover' && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute inset-y-0 right-0 mt-1 pr-3 flex items-center text-gray-400 hover:text-gray-200 focus:outline-none"
                  aria-label="Clear search query"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 8.586L4.95 3.536 3.536 4.95 8.586 10l-5.05 5.05 1.414 1.414L10 11.414l5.05 5.05 1.414-1.414L11.414 10l5.05-5.05-1.414-1.414L10 8.586z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* The "Search" button */}
          <div className="flex-shrink-0">
            <button
              type="submit"
              className={`w-full lg:w-auto mt-5 lg:mt-5 ${
                type === 'Discover'
                  ? 'px-6 py-2 rounded-full transition-all duration-300 bg-gradient-to-r from-orange-500 to-orange-700 shadow-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500'
                  : type === 'Stream'
                  ? 'px-6 py-2 rounded-full transition-all duration-300 bg-blue-800 hover:bg-blue-700 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500'
                  : 'px-6 py-2 rounded-full transition-all duration-300 bg-purple-800 hover:bg-purple-700 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500'
              }`}
            >
              Search
            </button>
          </div>
        </div>

        {/* MODE SELECTION: Download / Discover / Stream */}
        <div className="flex justify-center space-x-2 sm:space-x-4 mb-6 flex-wrap">
  {/* Download */}
  <button
    type="button"
    onClick={() => handleModeChange('Download')}
    className={`flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 px-4 sm:px-6 py-2 rounded-full transition-all duration-300 font-semibold text-sm sm:text-base ${
      type === 'Download'
        ? 'bg-purple-800 hover:bg-purple-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
        : 'bg-gray-700 text-gray-300 hover:bg-purple-800'
    }`}
    aria-label="Download Mode"
  >
    <FaDownload size={20} />
    <span className="text-center sm:text-left">Download</span>
  </button>

  {/* Discover */}
  <button
    type="button"
    onClick={() => handleModeChange('Discover')}
    className={`flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 px-4 sm:px-6 py-2 rounded-full transition-all duration-300 font-semibold text-sm sm:text-base ${
      type === 'Discover'
        ? 'bg-gradient-to-r from-orange-500 to-orange-700 shadow-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500'
        : 'bg-gray-700 text-gray-300 hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-700'
    }`}
    aria-label="Discover Mode"
  >
    <FaCompass size={20} />
    <span className="text-center sm:text-left">Discover</span>
  </button>

  {/* Stream */}
  <button
    type="button"
    onClick={() => handleModeChange('Stream')}
    className={`flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 px-4 sm:px-6 py-2 rounded-full transition-all duration-300 font-semibold text-sm sm:text-base ${
      type === 'Stream'
        ? 'bg-blue-800 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500'
        : 'bg-gray-700 text-gray-300 hover:bg-blue-800'
    }`}
    aria-label="Stream Mode"
  >
    <FaPlay size={20} />
    <span className="text-center sm:text-left">Stream</span>
  </button>
</div>

        {/* SERVER SELECTION (only if Stream/Download) */}
        {(type === 'Stream' || type === 'Download') && availableServers.length > 0 && (
          <div className="flex justify-center space-x-4 mb-6">
            {availableServers.map((serverOption) => (
              <button
                key={serverOption}
                type="button"
                onClick={() => handleServerChange(serverOption)}
                className={`flex items-center space-x-2 px-6 py-2 rounded-full transition-all duration-300 font-semibold ${
                  server === serverOption
                    ? isStreamServer(serverOption)
                      ? 'bg-purple-500 text-white'
                      : 'bg-blue-500 text-white'
                    : `bg-gray-700 text-gray-300 ${getHoverColor(serverOption)}`
                }`}
                aria-label={`${serverOption} Server`}
              >
                {serverIcons[serverOption]}
                <span>{serverOption}</span>
              </button>
            ))}
          </div>
        )}

        {/* Error Message if query is empty */}
        {error && <p className="text-error text-sm mb-4">{error}</p>}

        {/* Additional Filters */}
        {type !== 'Discover' && combinedFilters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {combinedFilters.map((filter) => {
              if (filter.type === 'select') {
                // A dropdown
                return (
                  <div key={filter.name} className="flex flex-col">
                    <label className="text-sm text-gray-300 mb-1" htmlFor={filter.name}>
                      {filter.label}
                    </label>
                    <select
                      name={filter.name}
                      id={filter.name}
                      value={formValues[filter.name] || ''}
                      onChange={handleChange}
                      className="px-4 py-3 bg-gray-800 text-textPrimary rounded-md focus:outline-none focus:ring-2 focus:ring-accentBlue transition duration-300"
                      required={filter.required}
                    >
                      <option value="">Any</option>
                      {filter.options.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              } else {
                // Number / text field
                return (
                  <div key={filter.name} className="flex flex-col">
                    <label className="text-sm text-gray-300 mb-1" htmlFor={filter.name}>
                      {filter.label}
                    </label>
                    <input
                      type={filter.type}
                      name={filter.name}
                      id={filter.name}
                      placeholder={filter.label}
                      value={formValues[filter.name] || ''}
                      onChange={handleChange}
                      className="px-4 py-3 bg-gray-800 text-textPrimary rounded-md focus:outline-none focus:ring-2 focus:ring-accentBlue transition duration-300"
                      min={
                        ['season', 'yearfrom', 'yearto', 'year'].includes(filter.name)
                          ? '1'
                          : undefined
                      }
                      required={filter.required}
                    />
                  </div>
                );
              }
            })}
          </div>
        )}
      </form>
    </div>
  );
}
